/**
 * Single source of truth for the 5 portal origins in the multi-Amplify
 * topology + a strict allowlist validator used by every place that
 * could otherwise become an open-redirect vector.
 *
 * Origins
 * -------
 *   - `clients.portal.rook-connect.com`   — landing during cutover
 *                                            (Route53-owned, live now)
 *   - `clients.portal.tryrook.io`         — landing post-cutover
 *   - `production.clients.portal.tryrook.io`    — prod portal (cuenta 3 /
 *                                            us-east-1)
 *   - `sandbox.clients.portal.tryrook.io` — sandbox portal (cuenta 8 /
 *                                            us-east-1)
 *   - `development.clients.portal.tryrook.io`     — dev portal (cuenta 8 /
 *                                            us-east-2)
 *
 * Why a single source of truth
 * ----------------------------
 * Every other portal-related URL operation in the codebase MUST
 * resolve through here. Hardcoding origin strings in different files
 * = drift risk = the kind of bug where you remove `rook-connect` from
 * one place but leave it in another and only discover it months
 * later when an open-redirect attack succeeds because two files
 * disagreed. Defining the list once + exporting a validator forces
 * every callsite to share the same allowlist.
 *
 * Used by
 * -------
 *   - Landing's login handler: validates the `?redirect=` query
 *     param's origin is in the allowlist before issuing the post-
 *     auth redirect (CPF-NFR-028 open-redirect protection)
 *   - Per-env portal's preconnect-hints injector: knows which
 *     origins to render `<link rel="preconnect" href>` for
 *   - SPA's env-switcher component: constructs the destination URL
 *     from the matched origin
 *   - CORS allowOrigins in landing + portal HttpApi configs:
 *     same list, single grep target
 *
 * What this module is NOT
 * -----------------------
 *   - It does NOT decide cookie domain. That's separate
 *     (CPF-NFR-026) and depends on whether we're during cutover
 *     (`.portal.tryrook.io`) or post-cutover (`.clients.portal.
 *     tryrook.io`).
 *   - It does NOT know which origin is "the current" one. That's
 *     `window.location.origin` at the callsite.
 *   - It does NOT do any I/O. Pure data + pure functions; safe to
 *     import from both the SPA and the Lambdas.
 */

/**
 * CUTOVER FLAG — the single switch for the Vercel→Amplify transition.
 *
 * During the cutover, the per-env portals (and the landing) are served
 * under the Route53-owned `rook-connect` hosts. Because Route53 owns
 * these zones, AWS ACM auto-validates the certs (no CAA fight) and the
 * hosts resolve + serve TODAY for pre-cutover testing. At cutover
 * (RR-957) flip this to `false` and ALL envs serve under
 * `tryrook.io` for consistency — at that point the
 * `clients.portal.tryrook.io` CAA unblocks and the FINAL per-env hosts
 * get their certs.
 *
 * EVERYTHING that picks a "live" origin (the post-login handoff target,
 * the preconnect hints) derives from this one constant. **At cutover,
 * flip this to `false` — that is the only line that changes.**
 */
export const CUTOVER_IN_PROGRESS = true;

/**
 * Registrable-domain bases — each written ONCE; every host below derives from
 * them via template literals. This is the anti-"left-behind-at-cutover" rule:
 * NO host string is hardcoded twice, so the cutover never leaves a stale
 * domain. ALL portal hosts share ONE registrable domain per base — REQUIRED so
 * the single session cookie (`Domain=.<base>`) reaches the landing + all 3
 * dashboards (cookie.ts / P-029). Different TLDs per env (.review/.dev) would
 * break that cookie, so those stay for the DATA/API hosts only, never here.
 */
const ROOK_CONNECT_BASE = "clients.portal.rook-connect.com"; // Route53-owned; live now (no CAA fight)
const TRYROOK_BASE = "clients.portal.tryrook.io"; // public; becomes live at cutover

/**
 * The base that resolves + serves RIGHT NOW. The ONLY thing the cutover flips:
 * set `CUTOVER_IN_PROGRESS = false` and every live host below re-points — no
 * other line changes, nothing gets left on the old domain.
 */
export const LIVE_BASE = CUTOVER_IN_PROGRESS ? ROOK_CONNECT_BASE : TRYROOK_BASE;

/**
 * The 8 trusted portal origins — the open-redirect allowlist that
 * `isTrustedPortalOrigin` checks the `?redirect=` param against (CPF-NFR-028).
 * Explicit + byte-exact (the security contract) but built from the two base
 * consts so the registrable domain is never duplicated. Contains BOTH bases so
 * a deep link to either is accepted during and after cutover — they are all
 * ours. After cutover the `rook-connect` hosts simply stop resolving in DNS
 * (harmless; RR-957 can prune them).
 */
export const PORTAL_ORIGINS = [
  // Landing — both bases
  `https://${ROOK_CONNECT_BASE}`,
  `https://${TRYROOK_BASE}`,
  // Per-env portals — rook-connect (live now)
  `https://production.${ROOK_CONNECT_BASE}`,
  `https://sandbox.${ROOK_CONNECT_BASE}`,
  `https://development.${ROOK_CONNECT_BASE}`,
  // Per-env portals — tryrook (post-cutover)
  `https://production.${TRYROOK_BASE}`,
  `https://sandbox.${TRYROOK_BASE}`,
  `https://development.${TRYROOK_BASE}`,
] as const;

export type PortalOrigin = (typeof PORTAL_ORIGINS)[number];

/** Per-env origins for a base, ordered [production, sandbox, development]. */
const perEnvFor = (
  base: typeof ROOK_CONNECT_BASE | typeof TRYROOK_BASE,
): readonly PortalOrigin[] => [
  `https://production.${base}`,
  `https://sandbox.${base}`,
  `https://development.${base}`,
];

/** Per-env portal origins — the Route53-owned `rook-connect` hosts (live now). */
export const PER_ENV_PORTAL_ORIGINS_ROOKCONNECT = perEnvFor(ROOK_CONNECT_BASE);

/** Per-env portal origins — FINAL `tryrook.io` hosts (post-cutover target). */
export const PER_ENV_PORTAL_ORIGINS_FINAL = perEnvFor(TRYROOK_BASE);

/**
 * The LIVE per-env portal origins — what the post-login handoff targets and the
 * preconnect hints warm. Derived from `LIVE_BASE`, so the single
 * `CUTOVER_IN_PROGRESS` switch moves all of them at once. `[0]` is Production.
 */
export const PER_ENV_PORTAL_ORIGINS: readonly PortalOrigin[] =
  perEnvFor(LIVE_BASE);

/**
 * The LIVE landing origin. Anything that needs "the landing URL" (per-env
 * portal's logout / expired-session redirect) imports THIS — never a hardcoded
 * literal — so cutover moves it via the one flag.
 */
export const LANDING_ORIGIN: PortalOrigin = `https://${LIVE_BASE}`;

/**
 * Both landing origins. Used by the per-env portal's preconnect-hints injector —
 * preconnect to BOTH so logout / expired-session redirect is instant regardless
 * of which landing base is live.
 */
export const LANDING_ORIGINS: readonly PortalOrigin[] = [
  `https://${ROOK_CONNECT_BASE}`,
  `https://${TRYROOK_BASE}`,
];

/**
 * Check whether a URL's origin is in the trusted allowlist. Returns
 * `true` for any of the 5 portal origins, `false` for everything
 * else INCLUDING malformed URLs, javascript: schemes, or origins
 * that look similar but aren't byte-exact ("clients.portal.tryrook.IO"
 * lowercased to ".io" still rejects because the host comparison is
 * case-sensitive at the URL spec level — modern URL parsers
 * lowercase the host but a custom string comparison would not, so
 * we rely on the URL parser).
 *
 * Strict equality on the parsed `.origin` field is the right check —
 * substring matching ("contains clients.portal.tryrook.io") is a
 * classic open-redirect bypass (an attacker registers
 * `clients.portal.tryrook.io.evil.example.com` and `endsWith` /
 * `includes` accept it). The URL.origin getter normalises the scheme
 * + host + port to a comparable form.
 *
 * Catches:
 *   - Malformed URLs (URL constructor throws → false)
 *   - Schemes other than https: (javascript:, data:, file:, etc.)
 *   - Origins not in the allowlist
 *   - Subdomain attacks like `production.clients.portal.tryrook.io.evil.com`
 *
 * Does NOT catch (intentionally):
 *   - URLs with credentials embedded (`https://user:pass@prod...`) —
 *     URL.origin strips those; the redirect goes to the bare origin
 *   - URLs with weird paths (`...prod.../etc/passwd`) — paths are
 *     allowed because the legitimate redirect uses the path to land
 *     the customer back on their original view
 *   - URLs with weird query strings — same reason as paths
 */
export const isTrustedPortalOrigin = (urlString: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(urlString);
  } catch {
    return false;
  }
  // Reject anything that isn't https — the portal origins are
  // https-only and this protects against javascript: / data: / etc.
  if (parsed.protocol !== "https:") {
    return false;
  }
  return (PORTAL_ORIGINS as readonly string[]).includes(parsed.origin);
};

/**
 * Map a customer-facing `Environment` to its LIVE per-env portal origin.
 *
 * "LIVE" means the host that actually resolves + serves today — derived
 * from `PER_ENV_PORTAL_ORIGINS` which follows `CUTOVER_IN_PROGRESS`. So
 * during cutover this returns the `rook-connect` hosts;
 * post-cutover (flip the flag) it returns the `clients.portal.tryrook.io`
 * hosts. Single switch, no per-callsite branching.
 *
 * Used by the per-env portal's `<EnvSwitcher>` (Topbar): the dropdown
 * renders OTHER envs as `<EnvSwitchButton targetOrigin={portalOriginForEnv(opt)}>`
 * so clicking another env is a cross-origin navigation (CPF-NFR-028),
 * never a path-flip on the same host (which would 5xx because the
 * prod-issued bearer token is rejected by the sandbox upstream — see
 * `foundations/AI-PITFALLS.md` P-033).
 *
 * Why an index lookup and not a `Record<Environment, PortalOrigin>` literal:
 * the source of truth is the ordered `PER_ENV_PORTAL_ORIGINS` array
 * (production, sandbox, development). A separate Record would duplicate
 * that data and drift at cutover. The order is pinned by tests in
 * `portalOrigins.test.ts` so a reordering breaks loud.
 */
import type { Environment } from "./appContext";

const ENV_TO_PORTAL_ORIGIN_INDEX: Record<Environment, 0 | 1 | 2> = {
  production: 0,
  sandbox: 1,
  development: 2,
};

export const portalOriginForEnv = (env: Environment): PortalOrigin =>
  PER_ENV_PORTAL_ORIGINS[ENV_TO_PORTAL_ORIGIN_INDEX[env]];

/**
 * Build `<link rel="preconnect" crossorigin>` + `<link rel="dns-prefetch">`
 * hint strings for an array of origins. Used by the SSR /
 * build-time HTML template injectors in the landing + per-env
 * portal packages.
 *
 * Returns an array of HTML link tag strings (no JSX dependency — the
 * function is callable from both client and server contexts).
 * Caller is responsible for joining + inserting into the `<head>`.
 *
 * The `crossorigin` attribute on preconnect is required for the TLS
 * connection to be reusable by authenticated navigations — without
 * it the browser opens a credential-less preconnect that doesn't
 * help when the navigation needs to send a cookie.
 */
export const buildPreconnectHints = (
  origins: readonly PortalOrigin[],
): readonly string[] => {
  const tags: string[] = [];
  for (const origin of origins) {
    tags.push(`<link rel="preconnect" href="${origin}" crossorigin>`);
  }
  for (const origin of origins) {
    tags.push(`<link rel="dns-prefetch" href="${origin}">`);
  }
  return tags;
};
